import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { findWhere, sortBy, pairs, map, uniq } from "underscore";
import { useRef } from "react";

import useQueriedRecords from "../../../../../api/api-hooks/records/useQueriedRecords";
import { AvailableModuleData } from "../../ReferencesHooks/useAvailableModules";
import { Field, Module } from "../../../../../types/TenantInterfaces";
import AllReferencesPagination from "./AllReferencesPagination";
import QueryUtils from "../../../../../utils/QueryUtils";
import useUserRecordActivity from "./useRecordActivity";
import CreatedReferences from "./CreatedReferences";
import NewReferences from "./NewReferences";
import AllReferences from "./AllReferences";

interface ManageSelectedParentProps {
  setSelectedSubitemParent: Dispatch<
    SetStateAction<Record<string, any> | null>
  >;
  records: Record<string, any>[];
  recordsLoading: boolean;
  selected: any;
}

// For loading the selected parent record if there are already selected values.
// All selected values must use the parent record for a selected parent record to be set.
const useManageSelectedParent = ({
  setSelectedSubitemParent,
  records,
  recordsLoading,
  selected,
}: ManageSelectedParentProps) => {
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (isFirstLoad.current === false) return;

    if (recordsLoading || !records || !records?.length) return;

    isFirstLoad.current = false;

    const values = Array.isArray(selected) ? selected : [selected];
    const recordID = values?.[0]?.id;

    // Ensure all values are Subitem References with the same Parent ID.
    for (const value of values) {
      if (!value) continue;

      if (!value?._isSubitem || value?.id !== recordID) {
        return;
      }
    }

    const parentRecord = findWhere(records, { id: recordID });

    if (!parentRecord) return;

    setSelectedSubitemParent(parentRecord);
  }, [records, recordsLoading, selected, setSelectedSubitemParent]);
};

interface SelectableReferencesProps {
  currentModule: Module;
  currentKey: string;
  modulesMap: Map<string, AvailableModuleData>;
  field: Field;
  query: any;
  setQuery: Dispatch<SetStateAction<any>>;
  selected: any;
  setSelected: Dispatch<SetStateAction<any>>;
  setKeepOpen: Dispatch<SetStateAction<boolean>>;
  setHideReference: Dispatch<SetStateAction<boolean>>;
  oneTap: boolean;
  onClose: () => void;
  createdRecords: any[];
  selectedSubitemParent: any;
  setSelectedSubitemParent: Dispatch<SetStateAction<any>>;
  setShowFieldSettings: null | Dispatch<SetStateAction<boolean>>;
  tenantID?: string;
  showDisplayButton?: boolean;
}

const tabs: { title: string; value: Tabs }[] = [
  {
    title: "All",
    value: "All",
  },
  {
    title: "Recent",
    value: "Recent",
  },
  {
    title: "Recently Viewed",
    value: "RecentlyViewed",
  },
  {
    title: "Favorites",
    value: "Favorites",
  },
];

const tabValues = tabs.flatMap((t) => {
  if (t.title === "All") return [];
  return [t.value];
});

export type Tabs = "All" | "Recent" | "RecentlyViewed" | "Favorites";

const SelectableReferences = ({
  currentModule,
  currentKey,
  modulesMap,
  field,
  query,
  setQuery,
  selected,
  setSelected,
  setKeepOpen,
  setHideReference,
  oneTap,
  onClose,
  createdRecords,
  selectedSubitemParent,
  setSelectedSubitemParent,
  setShowFieldSettings,
  tenantID,
  showDisplayButton,
}: SelectableReferencesProps) => {
  const [selectedTab, setTab] = useState<Tabs>("All");
  const isPublicForm = window.location.pathname.startsWith("/form");
  const { userRecordActivity, loading } = useUserRecordActivity();
  const splitKey = currentKey?.split?.(".");

  const isSubitemReference = splitKey?.length > 1;

  const gcUsers = field?.GcUsers && currentModule?.APIName === "Contacts";

  const GetRecordIDs = (type: Tabs) => {
    if (!userRecordActivity || !userRecordActivity[currentModule?.APIName])
      return [];

    const moduleRecords = userRecordActivity[currentModule?.APIName];

    let recordIDs: string[] = [];

    if (type === "Recent") {
      recordIDs =
        map(
          sortBy(pairs(moduleRecords[type]), (array) => array[1]),
          (array) => array[0]
        ) ?? [];
    } else {
      // @ts-ignore
      recordIDs = (moduleRecords[type] ?? ([] as string[])).reverse();
    }

    return recordIDs;
  };

  const finalTabs = useMemo(() => {
    if (isSubitemReference || isPublicForm) {
      setTab("All");
      return [tabs[0]];
    }

    return tabs;
  }, [isSubitemReference]);

  const gcUsersQuery = useMemo(() => {
    if (!gcUsers) return null;
    if (!query) return null;

    const newQuery = structuredClone(query);
    const filter = { Field: "_TenantUser", Method: "equals", Value: true };

    if (!newQuery.Filters.length) {
      newQuery.Filters.push({
        Filters: [filter],
        Group: true,
      });
    } else {
      newQuery.Filters[0].Filters.push(filter);
    }

    return newQuery;
  }, [gcUsers, query]);

  // Filter records to only records that have subitems.
  const subitemQuery = useMemo(() => {
    if (!query || !isSubitemReference) return null;

    const subitemAPIName = splitKey[1];

    const newQuery = structuredClone(query);

    newQuery.Fields.push(subitemAPIName);

    newQuery.Filters = [
      {
        Filters: [
          { Field: subitemAPIName, Method: "is-not-empty", Value: null },
          { Field: subitemAPIName, Method: "not-equals", Value: [] },
        ],
        Group: true,
      },
    ];

    return newQuery;
  }, [query, isSubitemReference, splitKey]);

  const finalQuery = useMemo(() => {
    let baseQuery = structuredClone(gcUsersQuery || subitemQuery || query);

    if (tabValues.includes(selectedTab)) {
      let topLevelFilter = [
        {
          Group: true,
          Filters: [
            {
              Field: "_id",
              Method: "in",
              Value: GetRecordIDs(selectedTab),
            },
          ],
        },
      ];

      baseQuery = QueryUtils.mergeTopLevelFilter(topLevelFilter, baseQuery);
    }

    const fields = userRecordActivity?.[currentModule?.APIName]?.Fields ?? [];

    if (fields?.length) {
      baseQuery.Fields = uniq(baseQuery.Fields.concat(fields));
    }

    return baseQuery;
  }, [
    query,
    gcUsersQuery,
    subitemQuery,
    loading,
    selectedTab,
    userRecordActivity?.[currentModule?.APIName],
  ]);

  const { records, pagination, recordsLoading } = useQueriedRecords({
    moduleAPIName: currentModule?.APIName,
    query: finalQuery,
    disabled: gcUsers && !gcUsersQuery,
    queryKeyID: selectedTab,
    cloudID: tenantID,
  });

  useManageSelectedParent({
    setSelectedSubitemParent,
    records,
    recordsLoading,
    selected,
  });

  return (
    <>
      <CreatedReferences />
      <NewReferences
        createdRecords={createdRecords}
        modulesMap={modulesMap}
        field={field}
        selected={selected}
        setSelected={setSelected}
        setKeepOpen={setKeepOpen}
        setHideReference={setHideReference}
        oneTap={oneTap}
        onClose={onClose}
      />
      <AllReferences
        records={
          selectedSubitemParent ? selectedSubitemParent[splitKey[1]] : records
        }
        currentKey={currentKey}
        modulesMap={modulesMap}
        field={field}
        recordsLoading={recordsLoading}
        selected={selected}
        setSelected={setSelected}
        setKeepOpen={setKeepOpen}
        setHideReference={setHideReference}
        oneTap={oneTap}
        onClose={onClose}
        isSubitemReference={isSubitemReference}
        selectedSubitemParent={selectedSubitemParent}
        setSelectedSubitemParent={setSelectedSubitemParent}
        tabs={finalTabs}
        setTab={setTab}
        selectedTab={selectedTab}
        userRecordActivity={userRecordActivity}
        setShowFieldSettings={setShowFieldSettings}
        showDisplayButton={showDisplayButton}
      />
      <AllReferencesPagination
        records={records}
        recordsLoading={recordsLoading}
        query={query}
        setQuery={setQuery}
        pagination={pagination}
      />
    </>
  );
};

export default SelectableReferences;
